import { useSyncExternalStore } from "react";
import type { ThemeMode } from "@carbon6/c6-frontend-design";
import { appTextModes } from "@/shared/ui/text";

// 텍스트 스타일 표의 단일 소유자는 `@/shared/ui/text` 다(modules 도 같은 값을 써야 해서).
// 기존 app 소비처가 계속 `@/app/theme` 에서 가져오도록 여기서 다시 내보낸다.
export { appTextModes };

const themeModeStorageKey = "spawning-pool:theme-mode";

// 테마는 앱 셸(ThemeProvider)과 사이드바 토글 등 여러 곳에서 읽고 쓰므로, 컴포넌트 로컬 state 가
// 아니라 단일 공유 스토어로 둔다 — 어디서 setThemeMode 하든 구독자 전체가 같은 값으로 동기화된다.
const themeModeListeners = new Set<() => void>();
let currentThemeMode: ThemeMode = readInitialThemeMode();

export function setThemeMode(mode: ThemeMode): void {
  if (mode === currentThemeMode) {
    return;
  }
  currentThemeMode = mode;
  try {
    window.localStorage.setItem(themeModeStorageKey, mode);
  } catch {
    // Storage can be unavailable in private or restricted browser contexts.
  }
  themeModeListeners.forEach((listener) => listener());
}

function subscribeThemeMode(listener: () => void): () => void {
  themeModeListeners.add(listener);
  return () => {
    themeModeListeners.delete(listener);
  };
}

export function useThemeModePreference(): readonly [ThemeMode, (mode: ThemeMode) => void] {
  const themeMode = useSyncExternalStore(
    subscribeThemeMode,
    () => currentThemeMode,
    () => currentThemeMode,
  );
  return [themeMode, setThemeMode] as const;
}

function readInitialThemeMode(): ThemeMode {
  // 스토어 초기값은 모듈 로드 시점에 한 번 계산된다 — window 없는 환경(테스트·SSR)에선 기본값으로 안전하게.
  if (typeof window === "undefined") {
    return "light";
  }
  const storedThemeMode = readStoredThemeMode();
  if (storedThemeMode !== null) {
    return storedThemeMode;
  }

  if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }

  return "light";
}

function readStoredThemeMode(): ThemeMode | null {
  try {
    const value = window.localStorage.getItem(themeModeStorageKey);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    return null;
  }
}
